import * as THREE from 'three';
import type { AudioEngine } from '../audio/AudioEngine';
import { FLOWERS } from '../game/flowers';
import { plotPosition } from '../game/grid';
import { CUSTOMERS } from '../game/orders';
import { UPGRADES } from '../game/upgrades';
import { LAYOUT } from '../render/layout';
import type { WorldRenderer } from '../render/WorldRenderer';
import { toast, ui } from '../ui/store';
import { WORLD_ACTOR, type ActorEvent, type GameController } from './GameController';
import type { PlayController } from './PlayController';

const MARKET_POINT = new THREE.Vector3(LAYOUT.market.x, 2.2, LAYOUT.market.z);
const BOARD_POINT = new THREE.Vector3(LAYOUT.board.x, 2.4, LAYOUT.board.z);

/**
 * Maps rules-engine events to everything the player sees and hears: particles,
 * floating text, sounds and toasts. Pure presentation — never touches state.
 */
export class EventEffects {
  constructor(
    private readonly game: GameController,
    private readonly world: WorldRenderer,
    private readonly play: PlayController,
    private readonly audio: AudioEngine,
  ) {
    game.events.on((events) => this.handle(events));
    game.rejected.on((reason) => {
      toast(reason, 'bad');
      audio.play('error');
    });
  }

  /** Display name for whoever caused an event ('' for the local player / world). */
  private who(actor: string): string {
    if (actor === this.game.localPlayerId || actor === WORLD_ACTOR) return '';
    return ui.players.value.find((p) => p.id === actor)?.name ?? 'A friend';
  }

  private plotVolume(plotId: number): number {
    const { x, z } = plotPosition(plotId);
    return this.play.volumeAt(x, z);
  }

  private handle(events: ActorEvent[]): void {
    let bloomSounds = 0;
    for (const { actor, event } of events) {
      const fx = this.world.particles;
      const name = this.who(actor);
      switch (event.type) {
        case 'planted': {
          fx.dust(this.world.plots.plotTop(event.plotId), 10);
          this.audio.play('plant', this.plotVolume(event.plotId));
          break;
        }
        case 'watered': {
          for (const plotId of event.plotIds) fx.water(this.world.plots.plotTop(plotId).setY(0.9));
          const first = event.plotIds[0];
          this.audio.play('water', first === undefined ? 1 : this.plotVolume(first));
          break;
        }
        case 'harvested': {
          const def = FLOWERS[event.species];
          const top = this.world.plots.plotTop(event.plotId);
          fx.petals(top.clone().setY(0.8), [...def.colors, def.center]);
          this.world.floating.spawn(top.clone().setY(1.6), `+1 ${def.emoji}`, 'item');
          if (event.golden) {
            fx.sparkles(top.clone().setY(1), 30);
            this.world.floating.spawn(top.clone().setY(2.1), '✨ Golden!', 'rare', 0.15);
            this.audio.play('golden', this.plotVolume(event.plotId));
          } else {
            this.audio.play('harvest', this.plotVolume(event.plotId));
          }
          if (event.seedsDropped > 0) {
            this.world.floating.spawn(
              top.clone().setY(1.6),
              `+${event.seedsDropped} 🌱`,
              'xp',
              0.35,
            );
          }
          if (event.hybrid) {
            const hybrid = FLOWERS[event.hybrid];
            fx.sparkles(top.clone().setY(1.2), 50, [...hybrid.colors]);
            toast(
              `🐝 Cross-pollination! ${name ? `${name} found` : 'You found'} a ${hybrid.emoji} ${hybrid.name} seed!`,
              'rare',
              5,
            );
            this.audio.play('discover');
          }
          break;
        }
        case 'bloomed': {
          const top = this.world.plots.plotTop(event.plotId);
          fx.sparkles(top.setY(1.1), event.golden ? 24 : 8);
          if (event.golden)
            toast(`✨ A golden ${FLOWERS[event.species].name} just bloomed!`, 'rare');
          if (bloomSounds++ < 2) this.audio.play('bloom', this.plotVolume(event.plotId));
          break;
        }
        case 'dug':
          fx.dust(this.world.plots.plotTop(event.plotId), 16);
          this.audio.play('dig', this.plotVolume(event.plotId));
          break;
        case 'plotUnlocked': {
          const top = this.world.plots.plotTop(event.plotId);
          fx.dust(top, 26);
          fx.sparkles(top.clone().setY(0.8), 12);
          this.world.floating.spawn(top.clone().setY(1.4), `-🪙${event.cost}`, 'coins');
          this.audio.play('unlock');
          break;
        }
        case 'seedsBought': {
          const def = FLOWERS[event.species];
          this.world.floating.spawn(MARKET_POINT, `+${event.qty} ${def.emoji} seeds`, 'item');
          this.audio.play('buy');
          break;
        }
        case 'sold':
          fx.coins(MARKET_POINT);
          this.world.floating.spawn(MARKET_POINT.clone().setY(2.8), `+🪙${event.earned}`, 'coins');
          this.audio.play('coin');
          break;
        case 'upgradeBought': {
          const def = UPGRADES[event.upgrade];
          fx.confetti(this.play.position.clone().setY(1.5));
          toast(`${def.emoji} ${def.name} is now level ${event.level}!`, 'good');
          this.audio.play('unlock');
          break;
        }
        case 'orderDelivered': {
          const customer = CUSTOMERS[event.customer] ?? CUSTOMERS[0]!;
          fx.coins(BOARD_POINT, 18);
          fx.confetti(BOARD_POINT);
          this.world.floating.spawn(BOARD_POINT.clone().setY(3), `+🪙${event.reward}`, 'coins');
          toast(
            `${customer.emoji} ${customer.name} loves ${name ? `${name}'s` : 'your'} flowers! +🪙${event.reward}`,
            'good',
          );
          this.audio.play('order');
          break;
        }
        case 'newOrder': {
          const customer = CUSTOMERS[event.customer] ?? CUSTOMERS[0]!;
          toast(`📜 New order from ${customer.emoji} ${customer.name}`);
          this.audio.play('chat');
          break;
        }
        case 'discovered': {
          const def = FLOWERS[event.species];
          toast(`📖 New flower in your almanac: ${def.emoji} ${def.name}!`, 'rare');
          this.audio.play('discover');
          break;
        }
        case 'levelUp':
          ui.levelUp.value = event.level;
          fx.confetti(this.play.position.clone().setY(1.6));
          this.audio.play('levelUp');
          break;
        case 'newDay': {
          const def = FLOWERS[event.featured];
          toast(
            `☀️ Day ${event.day} — ${def.emoji} ${def.name}s are in demand at the market!`,
            'info',
            5,
          );
          break;
        }
        case 'rainStart':
          toast('🌧️ Rain! Every flower is getting a drink.');
          break;
        case 'rainStop':
          toast('🌈 The rain stopped — look, a rainbow!', 'rare');
          break;
        case 'sprinklers':
          this.world.spray();
          break;
        case 'slept':
          ui.sleeping.value = true;
          setTimeout(() => (ui.sleeping.value = false), 1600);
          toast(
            `😴 ${name ? `${name} rang the bedtime bell. ` : ''}Good morning! ${event.bloomed} flower${event.bloomed === 1 ? '' : 's'} bloomed overnight.`,
            'good',
            5,
          );
          break;
        case 'farmRenamed':
          toast(`🏡 Welcome to ${event.name}!`, 'good');
          break;
        case 'orderDiscarded':
          break;
      }
    }
  }
}
